import { useMemo, useState } from 'react';
import AdminModal from '../components/AdminModal';
import ConfirmDialog from '../components/ConfirmDialog';
import AdminPagination from '../components/AdminPagination';
import AdminSteps from '../components/AdminSteps';
import { usePagination } from '../hooks/usePagination';
import { useAdminPersist } from '../AdminPersistContext';
import { useEditorFeedback } from '../hooks/useEditorFeedback';
import { useNotifyFormDirty } from '../hooks/useNotifyFormDirty';

const PAGE_SIZE = 8;

const emptyTestimonial = () => ({
  id: Date.now(),
  quote: '"Your testimonial quote here."',
  audioUrl: '',
});

export default function TestimonialsEditor({ content, onChange }) {
  const items = content.testimonials?.items ?? [];
  const [editId, setEditId] = useState(null);
  const [isNew, setIsNew] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [formError, setFormError] = useState('');
  const { persistNow } = useAdminPersist();
  const feedback = useEditorFeedback({ persistNow });

  const { page, setPage, totalPages, paginatedItems, totalItems } = usePagination(items, PAGE_SIZE);

  const patchItems = (next) => {
    onChange({
      ...content,
      testimonials: { items: next },
    });
  };

  const editing = useMemo(
    () => (editId !== null ? items.find((t) => String(t.id) === String(editId)) : null),
    [items, editId]
  );

  const openAdd = () => {
    const item = emptyTestimonial();
    patchItems([item, ...items]);
    setEditId(item.id);
    setIsNew(true);
    setFormError('');
    setPage(1);
  };

  const saveItem = (form) => {
    const id = Number(form.id);
    if (!Number.isFinite(id) || id <= 0) {
      setFormError('ID must be a positive number (used for audio mapping).');
      return false;
    }
    const duplicate = items.some((t) => String(t.id) === String(id) && String(t.id) !== String(editId));
    if (duplicate) {
      setFormError(`Another testimonial already uses ID ${id}.`);
      return false;
    }
    const normalized = {
      id,
      quote: (form.quote || '').trim(),
      audioUrl: (form.audioUrl || '').trim(),
    };
    if (!normalized.quote) {
      setFormError('Quote is required.');
      return false;
    }
    patchItems(items.map((t) => (String(t.id) === String(editId) ? normalized : t)));
    setEditId(normalized.id);
    setIsNew(false);
    setFormError('');
    return true;
  };

  const discardModal = () => {
    if (isNew && editId !== null) {
      patchItems(items.filter((t) => String(t.id) !== String(editId)));
    }
    setEditId(null);
    setIsNew(false);
    setFormError('');
    feedback.setFormDirty(false);
  };

  const closeModal = () => {
    feedback.requestClose({ isNew, onDiscard: discardModal });
  };

  return (
    <div className="admin-panel">
      <div className="admin-panel__head">
        <div>
          <h2>Testimonials</h2>
          <p className="admin-panel__desc">
            Quote cards on the Home page and Case Studies page (4 per slide).
          </p>
        </div>
        <button type="button" className="admin-btn admin-btn--primary" onClick={openAdd}>
          Add testimonial
        </button>
      </div>

      <AdminSteps />

      <div className="admin-toolbar">
        <span className="admin-toolbar__meta">{totalItems} testimonial(s)</span>
      </div>

      <div className="admin-list">
        {paginatedItems.map((t) => (
          <div key={t.id} className="admin-list-item">
            <div>
              <strong>ID {t.id}</strong>
              <br />
              <span>{t.quote.slice(0, 96)}{t.quote.length > 96 ? '…' : ''}</span>
            </div>
            <div className="admin-list-actions">
              <button
                type="button"
                className="admin-btn admin-btn--ghost admin-btn--sm"
                onClick={() => {
                  setEditId(t.id);
                  setIsNew(false);
                  setFormError('');
                }}
              >
                Edit
              </button>
              <button
                type="button"
                className="admin-btn admin-btn--danger admin-btn--sm"
                onClick={() => setDeleteId(t.id)}
              >
                Remove
              </button>
            </div>
          </div>
        ))}
        {!paginatedItems.length ? (
          <p style={{ color: '#666', fontSize: '0.9rem', margin: 0 }}>No testimonials yet.</p>
        ) : null}
      </div>

      <AdminPagination
        page={page}
        totalPages={totalPages}
        totalItems={totalItems}
        pageSize={PAGE_SIZE}
        onPageChange={setPage}
      />

      <AdminModal
        open={editId !== null}
        onClose={closeModal}
        title={isNew ? 'Add testimonial' : `Edit testimonial #${editId}`}
        size="md"
      >
        {editing ? (
          <TestimonialForm
            key={String(editing.id)}
            item={editing}
            formError={formError}
            onSave={(form) => {
              if (saveItem(form)) {
                feedback.notifySaved('Testimonial saved to the server.');
                discardModal();
              }
            }}
            onCancel={closeModal}
            onDirtyChange={feedback.setFormDirty}
          />
        ) : null}
      </AdminModal>

      <ConfirmDialog
        open={deleteId !== null}
        title="Remove testimonial"
        message="Remove this testimonial from the site? Changes save to the server automatically."
        confirmLabel="Remove"
        cancelLabel="Cancel"
        danger
        onConfirm={() => {
          patchItems(items.filter((t) => String(t.id) !== String(deleteId)));
          if (String(editId) === String(deleteId)) setEditId(null);
          setDeleteId(null);
        }}
        onCancel={() => setDeleteId(null)}
      />

      {feedback.dialogs}
    </div>
  );
}

function TestimonialForm({ item, formError, onSave, onCancel, onDirtyChange }) {
  const [form, setForm] = useState({ ...item });
  useNotifyFormDirty(form, item, onDirtyChange);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onSave(form);
      }}
    >
      {formError ? <p className="admin-error">{formError}</p> : null}
      <div className="admin-field">
        <label>Numeric ID (unique)</label>
        <input
          type="number"
          value={form.id}
          onChange={(e) => set('id', Number(e.target.value))}
          required
        />
      </div>
      <div className="admin-field">
        <label>Quote</label>
        <textarea value={form.quote} onChange={(e) => set('quote', e.target.value)} rows={6} required />
      </div>
      <div className="admin-field">
        <label>Audio URL (optional — leave empty to use built-in clip by ID)</label>
        <input
          value={form.audioUrl || ''}
          onChange={(e) => set('audioUrl', e.target.value)}
          placeholder="https://..."
        />
      </div>
      <div className="admin-form-actions">
        <button type="button" className="admin-btn admin-btn--ghost" onClick={onCancel}>
          Cancel
        </button>
        <button type="submit" className="admin-btn admin-btn--primary">
          Save testimonial
        </button>
      </div>
    </form>
  );
}
